import React, { useEffect, useState } from "react"
import { Box, Typography, Divider, Grid } from "@mui/material"
import { useTheme } from "@mui/system"
import { Formik } from "formik"
import CommonModal from "components/CommonModal"
import AppTextField from "components/StyledComponents/AppTextField"
import AppSelect from "components/StyledComponents/AppSelect"
import AppButton from "components/StyledComponents/AppButton"
import ToggleSwitch from "components/StyledComponents/ToggleSwitch"
import MultipleSelect from "components/StyledComponents/MultipleSelect"
import ImageUploadField from "components/FileUpload/ImageUploadField"
import { useDispatch, useSelector } from "react-redux"
import {
  getCMSRestaurantDetailAction,
  getCMSRestaurantGroupListAction,
  getCMSUsersForAssignmentAction,
  createCMSRestaurantAction,
  updateCMSRestaurantAction,
  uploadCMSMediaAction
} from "redux/superAdmin/actions"
import { restaurantConfigurationColumns, timeZones } from "helpers/constant"
import { validationSchemaRestaurant } from "helpers/validationSchema"

const DEFAULT_TEMP_DATA = { van_enabled: true, show_advertisement: false }

const defaultValues = {
  name: "",
  location: "",
  owner: "",
  timezone: "America/New_York",
  lite: true,
  new_user_journey: true,
  new_user_journey_cms: true,
  rewards_enabled: true,
  enable_preshift_card: true,
  about: "",
  ceo_message: "",
  preshift_history_lookback_days: 7
}

const SectionHeader = ({ children }) => {
  const theme = useTheme()
  return (
    <Box sx={{ mb: 2, mt: 4 }}>
      <Typography
        variant="body2Regular"
        sx={{
          color: theme.palette.text.secondary,
          opacity: 0.6,
          textTransform: "uppercase",
          letterSpacing: "0.08em"
        }}>
        {children}
      </Typography>
      <Divider sx={{ borderColor: theme.palette.background.main, mt: 1 }} />
    </Box>
  )
}

const RestaurantFormContent = ({
  formik,
  isEdit,
  iconFile,
  setIconFile,
  setIconMediaId,
  theme,
  setIconUploading,
  selectedUserIds,
  setSelectedUserIds
}) => {
  const dispatch = useDispatch()

  const { cmsRestaurantGroups, cmsUsersForAssignment } = useSelector(
    (store) => store?.superAdmin ?? {}
  )

  const groupOptions = (cmsRestaurantGroups?.results || []).map((g) => ({
    label: g.name,
    value: g.id
  }))
  const userMenuItems = (cmsUsersForAssignment || []).map((u) => ({
    label: `${u.full_name} (${u.email})`,
    value: u.id
  }))

  const handleIconChange = (file) => {
    setIconFile(file)
    setIconUploading(true)
    const formData = new FormData()
    formData.append("media", file)
    formData.append("media_type", "IMAGE")
    formData.append("content_type", file.type || "image/png")
    dispatch(
      uploadCMSMediaAction({
        payload: formData,
        onSuccess: (data) => {
          setIconMediaId(data?.id || data?.media_id || null)
          setIconUploading(false)
        }
      })
    )
  }

  return (
    <Box sx={{ mt: 1, pb: 3 }}>
      <SectionHeader>General Info</SectionHeader>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <AppTextField
            id="name"
            name="name"
            fullWidth
            label="Restaurant Name"
            isRequired
            isUpperCase={true}
            isDisabled={isEdit}
            placeholder="Enter restaurant name"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <AppTextField
            id="location"
            name="location"
            fullWidth
            isUpperCase={true}
            label="Location"
            placeholder="City, State"
            value={formik.values.location}
            onChange={formik.handleChange}
            error={formik.touched.location && Boolean(formik.errors.location)}
            helperText={formik.touched.location && formik.errors.location}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MultipleSelect
            id="owner"
            name="owner"
            label="Restaurant Group"
            search={true}
            menuItems={groupOptions}
            isRequired={true}
            isUpperCase={true}
            value={formik.values.owner ? [formik.values.owner] : []}
            onChange={(e) => {
              const newValue = e.target.value
              const singleValue = newValue.length > 0 ? newValue[newValue.length - 1] : ""
              formik.setFieldValue("owner", singleValue)
            }}
            error={formik.touched.owner && formik.errors.owner}
            touched={formik.touched.owner}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <AppSelect
            id="timezone"
            name="timezone"
            label="Timezone"
            isRequired
            isUpperCase={false}
            menuItems={timeZones.map((tz) => ({ label: tz, value: tz }))}
            value={formik.values.timezone}
            onMenuClick={(item) => formik.setFieldValue("timezone", item.value)}
            onChange={formik.handleChange}
            error={formik.touched.timezone && Boolean(formik.errors.timezone)}
          />
        </Grid>
      </Grid>

      <SectionHeader>Configuration</SectionHeader>
      <Grid container spacing={2}>
        {restaurantConfigurationColumns.map(({ key, label }) => (
          <Grid item xs={12} md={3} key={key}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                border: `1px solid ${theme.palette.aqua.main}30`,
                borderRadius: "8px",
                px: 2,
                py: 1.5
              }}>
              <ToggleSwitch
                id={key}
                name={key}
                checked={formik.values[key]}
                onChange={(e) => formik.setFieldValue(key, e.target.checked)}
              />
              <Typography variant="body2Regular" sx={{ color: theme.palette.text.primary }}>
                {label}
              </Typography>
            </Box>
          </Grid>
        ))}
        <Grid item xs={12}>
          <AppTextField
            id="preshift_history_lookback_days"
            name="preshift_history_lookback_days"
            type="number"
            isUpperCase={true}
            label="Pre-shift Lookback Days"
            placeholder="Days of pre-shift history to show"
            value={formik.values.preshift_history_lookback_days}
            onChange={formik.handleChange}
          />
        </Grid>
      </Grid>

      <SectionHeader>User Assignment</SectionHeader>
      <Grid container sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <MultipleSelect
            label="Platform Users"
            search
            menuItems={[{ label: "All Users", value: "all" }, ...userMenuItems]}
            value={selectedUserIds}
            placeholder="Select users"
            onChange={(e) => setSelectedUserIds(e.target.value)}
          />
        </Grid>
      </Grid>
      <SectionHeader>Icon / Media</SectionHeader>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ImageUploadField
            label="Restaurant Icon"
            placeholder="Upload icon (PNG, JPG, SVG)"
            value={iconFile}
            isUpperCase={true}
            onChange={handleIconChange}
            onDelete={() => {
              setIconFile(null)
              setIconMediaId(null)
            }}
            isVariant
          />
        </Grid>
      </Grid>
      <SectionHeader>Optional Info</SectionHeader>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <AppTextField
            id="about"
            name="about"
            fullWidth
            multiline
            rows={3}
            isUpperCase={true}
            label="About"
            placeholder="About the restaurant"
            value={formik.values.about}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <AppTextField
            id="ceo_message"
            name="ceo_message"
            fullWidth
            multiline
            rows={3}
            isUpperCase={true}
            label="CEO Message"
            placeholder="Message from the CEO"
            value={formik.values.ceo_message}
            onChange={formik.handleChange}
          />
        </Grid>
      </Grid>
    </Box>
  )
}
const ActionButtons = ({ iconUploading, formik, isEdit }) => {
  return (
    <Box className="d-flex align-items-center justify-content-center mb-3">
      <AppButton
        onClick={formik.handleSubmit}
        disabled={iconUploading}
        sx={{ padding: "14px 40px" }}>
        {isEdit ? "Update Restaurant" : "Create Restaurant"}
      </AppButton>
    </Box>
  )
}

const RestaurantFormModal = ({ open, onClose, restaurantId }) => {
  const dispatch = useDispatch()
  const isEdit = Boolean(restaurantId)
  const theme = useTheme()

  const [iconFile, setIconFile] = useState(null)
  const [iconMediaId, setIconMediaId] = useState(null)
  const [iconUploading, setIconUploading] = useState(false)
  const [selectedUserIds, setSelectedUserIds] = useState([])

  const { cmsRestaurantDetail, cmsUsersForAssignment } = useSelector(
    (store) => store?.superAdmin ?? {}
  )

  useEffect(() => {
    if (!open) return
    setIconFile(null)
    setIconMediaId(null)
    setIconUploading(false)
    setSelectedUserIds([])
    dispatch(getCMSRestaurantGroupListAction({}))
    dispatch(getCMSUsersForAssignmentAction({}))
    if (isEdit) {
      dispatch(getCMSRestaurantDetailAction({ id: restaurantId }))
    }
  }, [open, restaurantId])

  useEffect(() => {
    if (isEdit && cmsRestaurantDetail && String(cmsRestaurantDetail.id) === String(restaurantId)) {
      if (cmsRestaurantDetail.icon_url) {
        setIconFile({ url: cmsRestaurantDetail.icon_url })
      }
      if (cmsRestaurantDetail.assigned_user_ids?.length) {
        setSelectedUserIds(cmsRestaurantDetail.assigned_user_ids.map((uid) => String(uid)))
      }
    }
  }, [cmsRestaurantDetail])

  function handleSubmit(values) {
    const payload = {
      name: values.name,
      location: values.location || null,
      owner: values.owner || null,
      timezone: values.timezone,
      lite: values.lite,
      new_user_journey: values.new_user_journey,
      new_user_journey_cms: values.new_user_journey_cms,
      rewards_enabled: values.rewards_enabled,
      enable_preshift_card: values.enable_preshift_card,
      about: values.about || null,
      ceo_message: values.ceo_message || null,
      preshift_history_lookback_days: values.preshift_history_lookback_days
        ? parseInt(values.preshift_history_lookback_days)
        : null,
      temp_data: DEFAULT_TEMP_DATA,
      bulk_user_ids: selectedUserIds.includes("all")
        ? (cmsUsersForAssignment || []).map((u) => u.id)
        : selectedUserIds.filter((id) => id !== "all")
    }
    if (iconMediaId) {
      payload.icon_id = iconMediaId
    }
    if (isEdit) {
      dispatch(updateCMSRestaurantAction({ id: restaurantId, payload, onSuccess: onClose }))
    } else {
      dispatch(createCMSRestaurantAction({ payload, onSuccess: onClose }))
    }
  }

  const initialValues = isEdit
    ? {
        name: cmsRestaurantDetail?.name || "",
        location: cmsRestaurantDetail?.location || "",
        owner: cmsRestaurantDetail?.owner || "",
        timezone: cmsRestaurantDetail?.timezone || defaultValues.timezone,
        lite: cmsRestaurantDetail?.lite ?? defaultValues.lite,
        new_user_journey: cmsRestaurantDetail?.new_user_journey ?? defaultValues.new_user_journey,
        new_user_journey_cms:
          cmsRestaurantDetail?.new_user_journey_cms ?? defaultValues.new_user_journey_cms,
        rewards_enabled: cmsRestaurantDetail?.rewards_enabled ?? defaultValues.rewards_enabled,
        enable_preshift_card:
          cmsRestaurantDetail?.enable_preshift_card ?? defaultValues.enable_preshift_card,
        about: cmsRestaurantDetail?.about || "",
        ceo_message: cmsRestaurantDetail?.ceo_message || "",
        preshift_history_lookback_days:
          cmsRestaurantDetail?.preshift_history_lookback_days ??
          defaultValues.preshift_history_lookback_days
      }
    : defaultValues

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={validationSchemaRestaurant}
      onSubmit={handleSubmit}>
      {(formik) => (
        <CommonModal
          open={open}
          onClose={onClose}
          maxWidth="lg"
          actionButtons={
            <ActionButtons
              theme={theme}
              iconUploading={iconUploading}
              formik={formik}
              onClose={onClose}
              isEdit={isEdit}
            />
          }>
          <Box className="d-flex align-items-center justify-content-center">
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {isEdit ? "Edit Restaurant" : "Add Restaurant"}
            </Typography>
          </Box>

          <RestaurantFormContent
            formik={formik}
            theme={theme}
            isEdit={isEdit}
            iconFile={iconFile}
            setIconFile={setIconFile}
            setIconMediaId={setIconMediaId}
            iconUploading={iconUploading}
            setIconUploading={setIconUploading}
            selectedUserIds={selectedUserIds}
            setSelectedUserIds={setSelectedUserIds}
            onClose={onClose}
          />
        </CommonModal>
      )}
    </Formik>
  )
}

export default RestaurantFormModal
